import type { Checkpoint, GhostSample, RaceState } from './RaceTypes';
import { GhostRecorder } from './GhostRecorder';

export type Rival = {
  id: string;
  name: string;
  targetTime: number;
  aggression: number;
};

export class RaceManager {
  private state: RaceState = {
    active: false,
    time: 0,
    bestTime: null,
    checkpointIndex: 0,
    splits: [],
    completed: false,
    laps: [],
    currentLap: 0,
    rivals: [],
    playerPlace: null
  } as any;

  private readonly recorder = new GhostRecorder();
  private checkpointLastTime = 0;
  private totalLaps = 1;
  private lapStartTime = 0;

  start(rivals?: Rival[]) {
    this.state = {
      active: true,
      time: 0,
      bestTime: this.state.bestTime,
      checkpointIndex: 0,
      splits: [],
      completed: false,
      laps: [],
      currentLap: 1,
      rivals: rivals ?? [],
      playerPlace: null
    } as any;

    this.checkpointLastTime = 0;
    this.lapStartTime = 0;
    this.recorder.reset();
  }

  setLaps(totalLaps: number) {
    this.totalLaps = Math.max(1, Math.floor(totalLaps));
  }

  stop() {
    this.state.active = false;
  }

  update(dt: number) {
    if (!this.state.active) return;
    this.state.time += dt;
  }

  recordGhost(position: { x: number; y: number; z: number }, heading: number) {
    if (!this.state.active) return;
    this.recorder.record(this.state.time, position, heading);
  }

  checkCheckpoint(checkpoints: Checkpoint[], position: { x: number; y: number; z: number }) {
    if (!this.state.active || this.state.completed) return;

    const current = checkpoints[this.state.checkpointIndex];
    if (!current) return;

    const dx = position.x - current.position.x;
    const dz = position.z - current.position.z;
    const distSq = dx * dx + dz * dz;

    if (distSq <= current.radius * current.radius) {
      const t = this.state.time;
      const split = this.checkpointLastTime === 0 ? t : t - this.checkpointLastTime;
      this.checkpointLastTime = t;
      this.state.splits.push(split);
      this.state.checkpointIndex++;

      if (this.state.checkpointIndex >= checkpoints.length) {
        this.completeLap();
      }
    }
  }

  private completeLap() {
    this.state.laps.push({
      number: this.state.currentLap,
      startTime: this.lapStartTime,
      finishTime: this.state.time,
      splits: [...this.state.splits],
      personal_best: false
    });

    this.state.splits = [];
    this.checkpointLastTime = this.state.time;
    this.lapStartTime = this.state.time;

    if (this.state.currentLap < this.totalLaps) {
      this.state.currentLap += 1;
      this.state.checkpointIndex = 0;
      return;
    }

    this.complete();
  }

  private complete() {
    this.state.completed = true;
    this.state.active = false;

    const total = this.state.time;
    if (this.state.bestTime === null || total < this.state.bestTime) {
      this.state.bestTime = total;
    }

    const rivals: Rival[] = (this.state as any).rivals ?? [];
    if (rivals.length > 0) {
      const better = rivals.filter((r) => r.targetTime < total).length;
      (this.state as any).playerPlace = Math.min(rivals.length + 1, better + 1);
    } else {
      (this.state as any).playerPlace = 1;
    }
  }

  getGhost(): GhostSample[] {
    return this.recorder.getSamples();
  }

  getState(): RaceState & { rivals?: Rival[]; playerPlace?: number | null } {
    return this.state as any;
  }
}
